/**
 * Hide and Seek role rules: how often each role may use each rule-governed
 * ability (or die) in a round — never, without limit, or up to a number of
 * times — and the helpers that count a player's uses against them.
 *
 * Shared by Hide and Seek and Hide and Seek Teams. Rules are set separately
 * for hiders and seekers; a hider who is tagged in Hide and Seek starts again
 * from zero under the seeker rules.
 */
import { CheatType } from '../constants/gameTypes';
export const HNS_RULE_LIMIT_MIN = 1;
export const HNS_RULE_LIMIT_MAX = 10;
/** Every rule, in the order admins and players see them. */
export const HNS_RULE_KEYS = [
    { key: 'stealth', label: 'Stealth' },
    { key: 'teleport', label: 'Teleport' },
    { key: 'quickTravel', label: 'Quick Travel' },
    { key: 'taxi', label: 'Taxi' },
    { key: 'intercede', label: 'Intercede' },
    { key: 'pull', label: 'Pull' },
    { key: 'knockback', label: 'Knockback' },
    { key: 'leap', label: 'Leap' },
    { key: 'deaths', label: 'Deaths' },
];
/**
 * Default role rules: every ability off. Deaths are unlimited, as dying
 * counted for nothing before the Deaths rule existed.
 */
export const DEFAULT_HNS_ROLE_RULES = {
    stealth: 'off',
    teleport: 'off',
    quickTravel: 'off',
    taxi: 'off',
    intercede: 'off',
    pull: 'off',
    knockback: 'off',
    leap: 'off',
    deaths: 'unlimited',
};
/**
 * Before limits, each rule was a boolean `<name>Allowed`. Rounds, scheduled
 * ruleset changes and game snapshots saved then still hold that shape.
 */
const LEGACY_RULE_KEYS = {
    stealth: 'stealthAllowed',
    teleport: 'teleportAllowed',
    quickTravel: 'quickTravelAllowed',
    taxi: 'taxiAllowed',
    intercede: 'intercedeAllowed',
    pull: 'pullAllowed',
    knockback: 'knockbackAllowed',
    leap: 'leapAllowed',
};
/**
 * Read one stored allowance: an old boolean (true = unlimited, false = off),
 * `'off'`, `'unlimited'` or a limit (clamped; below 1 is off). Undefined when
 * the value is none of these.
 */
export function normalizeHnsRuleAllowance(value) {
    if (value === 'off' || value === 'unlimited')
        return value;
    if (value === true)
        return 'unlimited';
    if (value === false)
        return 'off';
    if (typeof value === 'number' && Number.isFinite(value)) {
        const limit = Math.round(value);
        if (limit < HNS_RULE_LIMIT_MIN)
            return 'off';
        return Math.min(limit, HNS_RULE_LIMIT_MAX);
    }
    return undefined;
}
/**
 * Read one role's rules from storage, in the current or the old boolean shape.
 * A rule that is missing or unreadable gets its default.
 */
export function normalizeHnsRoleRules(raw) {
    const stored = raw && typeof raw === 'object' ? raw : {};
    const rules = { ...DEFAULT_HNS_ROLE_RULES };
    for (const { key } of HNS_RULE_KEYS) {
        const legacyKey = LEGACY_RULE_KEYS[key];
        const allowance = normalizeHnsRuleAllowance(stored[key])
            ?? (legacyKey ? normalizeHnsRuleAllowance(stored[legacyKey]) : undefined);
        if (allowance !== undefined)
            rules[key] = allowance;
    }
    return rules;
}
const RULE_KEY_SET = new Set(HNS_RULE_KEYS.map(({ key }) => key));
/**
 * Read a player's stored rule uses (a JSONB array, or its JSON text), oldest
 * first, dropping any entry that isn't a use.
 */
export function normalizeHnsRuleUses(raw) {
    let stored = raw;
    if (typeof stored === 'string') {
        try {
            stored = JSON.parse(stored);
        }
        catch {
            return [];
        }
    }
    if (!Array.isArray(stored))
        return [];
    return stored
        .filter((use) => !!use && typeof use === 'object'
        && RULE_KEY_SET.has(use.rule)
        && typeof use.at === 'number' && Number.isFinite(use.at))
        .map(({ rule, at }) => ({ rule, at }))
        .sort((a, b) => a.at - b.at);
}
/**
 * How an allowance reads to admins and players: "Off", "Unlimited" or "2".
 * Deaths that are off read "None allowed": dying can't be switched off.
 */
export function formatHnsRuleAllowance(allowance, rule) {
    if (allowance === 'off')
        return rule === 'deaths' ? 'None allowed' : 'Off';
    if (allowance === 'unlimited')
        return 'Unlimited';
    return String(allowance);
}
/**
 * The rule a detected cheat type falls under. Null when no rule covers it
 * (leaving the area, the camera distance, a banned mount, an admin's disqualification): those
 * always disqualify.
 */
export function cheatTypeToRuleKey(cheatType) {
    switch (cheatType) {
        case CheatType.STEALTH: return 'stealth';
        case CheatType.TELEPORT: return 'teleport';
        case CheatType.QUICK_TRAVEL: return 'quickTravel';
        case CheatType.TAXI: return 'taxi';
        case CheatType.INTERCEDE: return 'intercede';
        case CheatType.PULL: return 'pull';
        case CheatType.KNOCKBACK: return 'knockback';
        case CheatType.LEAP: return 'leap';
        case CheatType.DEATH: return 'deaths';
        default: return null;
    }
}
/**
 * Whose rules a player was playing by at a moment in the round. In Hide and
 * Seek a hider plays by the seeker rules from when they are tagged. In Hide
 * and Seek Teams a player's role comes from their team and never changes.
 */
export function getHideSeekRoleAtTime(player, atUtcMs) {
    if ('team' in player && player.team)
        return player.originalRole ?? player.role;
    if (player.originalRole === 'seeker')
        return 'seeker';
    if (player.capturedAt != null && atUtcMs >= player.capturedAt)
        return 'seeker';
    return 'hider';
}
/** How many times a player used a rule while playing by one role's rules. */
export function countRuleUses(player, rule, role, roleAtTime = getHideSeekRoleAtTime) {
    return (player.ruleUses ?? []).filter(use => use.rule === rule && roleAtTime(player, use.at) === role).length;
}
/** Whether one more use fits an allowance, given the uses already made. */
export function isRuleUseAllowed(allowance, usedBefore) {
    if (allowance === 'unlimited')
        return true;
    if (allowance === 'off')
        return false;
    return usedBefore < allowance;
}
/** Uses left under an allowance: null when unlimited, 0 when off or used up. */
export function ruleUsesLeft(allowance, used) {
    if (allowance === 'unlimited')
        return null;
    if (allowance === 'off')
        return 0;
    return Math.max(0, allowance - used);
}
